import React, { useState, useEffect, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { projectAPI } from '../api/projectAPI';
import './ProjectEditPage.css';

interface ProjectData {
  category: string;
  workType: string;
  title: string;
  shortDescription: string;
  budgetMin: number;
  budgetMax: number;
  budgetType: string;
  startDate: string;
  deadline: string;
  durationDays: number;
  description: string;
  requiredSkills: string[];
  experienceLevel: string;
}

const ProjectEditPage: React.FC = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useTranslation();
  const { user } = useAuth();
  
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState('');
  const [project, setProject] = useState<any>(null);
  const [skillInput, setSkillInput] = useState('');
  
  const [formData, setFormData] = useState<ProjectData>({
    category: '',
    workType: 'outsourcing',
    title: '',
    shortDescription: '',
    budgetMin: 0,
    budgetMax: 0,
    budgetType: 'project',
    startDate: '',
    deadline: '',
    durationDays: 7,
    description: '',
    requiredSkills: [],
    experienceLevel: 'beginner'
  });

  const formatDateForInput = (dateString: string | null): string => {
    if (!dateString) return '';
    try {
      return new Date(dateString).toISOString().split('T')[0];
    } catch { return ''; }
  };

  const loadProject = useCallback(async (projectId: number) => {
    try {
      setLoading(true);
      const response = await projectAPI.getProjectById(projectId);
      const projectData = response.data;
      setProject(projectData);
      setFormData({
        category: getCategoryFromId(projectData.category_id),
        workType: projectData.work_type || 'outsourcing',
        title: projectData.title,
        shortDescription: projectData.description,
        budgetMin: projectData.budget_min || 0,
        budgetMax: projectData.budget_max || 0,
        budgetType: projectData.budget_type || 'project',
        startDate: formatDateForInput(projectData.start_date),
        deadline: formatDateForInput(projectData.deadline),
        durationDays: projectData.duration_days || 7,
        description: projectData.description,
        requiredSkills: Array.isArray(projectData.required_skills)
          ? projectData.required_skills
          : (projectData.required_skills ? JSON.parse(projectData.required_skills) : []),
        experienceLevel: projectData.experience_level,
      });
    } catch (err: any) {
      console.error('프로젝트 로딩 실패:', err);
      setError(err.response?.data?.message || t('pjEdit.loadFailed'));
    } finally {
      setLoading(false);
    }
  }, [t]);

  useEffect(() => {
    if (id && user) loadProject(parseInt(id));
  }, [id, user, loadProject]);

  const getCategoryFromId = (categoryId: number): string => {
    const map: { [key: number]: string } = {
      1: 'design',
      2: 'development',
      3: 'marketing',
      4: 'writing',
      5: 'video',
      6: 'music',
      7: 'business',
      8: 'other',
    };
    return map[categoryId] || 'development';
  };

  const getCategoryId = (category: string): number => {
    const map: { [key: string]: number } = {
      'design': 1,
      'development': 2,
      'marketing': 3,
      'writing': 4,
      'video': 5,
      'music': 6,
      'business': 7,
      'other': 8,
    };
    return map[category] || 2;
  };

  const handleInputChange = (field: keyof ProjectData, value: any) => {
    console.log('입력 변경:', field, '=', value);
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  const addSkill = () => {
    if (skillInput.trim()) {
      setFormData(prev => ({
        ...prev,
        requiredSkills: [...(prev.requiredSkills || []), skillInput.trim()]
      }));
      setSkillInput('');
    }
  };

  const removeSkill = (index: number) => {
    setFormData(prev => ({
      ...prev,
      requiredSkills: prev.requiredSkills.filter((_, i) => i !== index)
    }));
  };

  const validateForm = (): boolean => {
    if (!formData.title.trim()) {
      setError(t('pjCreate.errTitle'));
      return false;
    }
    if (!formData.shortDescription.trim()) {
      setError(t('pjEdit.errDesc'));
      return false;
    }
    if (formData.budgetMin <= 0) {
      setError(t('pjEdit.errBudgetMin'));
      return false;
    }
    if (formData.budgetMax <= 0) {
      setError(t('pjEdit.errBudgetMax'));
      return false;
    }
    if (formData.budgetMin > formData.budgetMax) {
      setError(t('pjCreate.errBudgetOrder'));
      return false;
    }
    if (formData.startDate && formData.deadline && formData.startDate > formData.deadline) {
      setError(t('pjEdit.errDateOrder'));
      return false;
    }
    return true;
  };

  const handleSubmit = async () => {
    if (!validateForm()) {
      return;
    }

    try {
      setSaving(true);
      setError('');

      const updateData = {
        title: formData.title,
        category_id: getCategoryId(formData.category),
        work_type: formData.workType,
        description: formData.description,
        budget_min: formData.budgetMin,
        budget_max: formData.budgetMax,
        budget_type: formData.budgetType,
        start_date: formData.startDate || null,
        deadline: formData.deadline || null,
        duration_days: formData.durationDays,
        required_skills: JSON.stringify(formData.requiredSkills),
        experience_level: formData.experienceLevel
      };

      console.log('프로젝트 수정 데이터:', updateData);
      
      const response = await projectAPI.updateProject(parseInt(id!), updateData);
      console.log('프로젝트 수정 응답:', response);

      alert(t('pjEdit.updated'));
      navigate('/my-projects');

    } catch (err: any) {
      console.error('프로젝트 수정 실패:', err);
      setError(err.response?.data?.message || t('pjEdit.updateFailed'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="project-edit-page">
        <div className="container">
          <div className="loading">{t('pjEdit.loading')}</div>
        </div>
      </div>
    );
  }

  if (error && !project) {
    return (
      <div className="project-edit-page">
        <div className="container">
          <div className="error-message">{error}</div>
        </div>
      </div>
    );
  }

  return (
    <div className="project-edit-page">
      <div className="container">
        <button 
          className="back-button"
          onClick={() => navigate('/my-projects')}
        >
          ← {t('serviceDetail.backToList')}
        </button>

        <div className="page-header">
          <h1>✏️ {t('pjEdit.title')}</h1>
          <p>{t('pjEdit.subtitle')}</p>
        </div>

        <div className="form-container">
          {/* 기본 정보 */}
          <div className="form-section">
            <h2>{t('profile.basicInfo')}</h2>
            
            <div className="form-group required">
              <label>{t('pjCreate.stepCategory')}</label>
              <select
                value={formData.category}
                onChange={(e) => handleInputChange('category', e.target.value)}
              >
                <option value="">{t('pjEdit.categoryPlaceholder')}</option>
                <option value="development">{t('pjEdit.catDev')}</option>
                <option value="design">{t('pjEdit.catDesign')}</option>
                <option value="marketing">{t('pjEdit.catMarketing')}</option>
                <option value="writing">{t('pjEdit.catWriting')}</option>
                <option value="video">{t('pjEdit.catVideo')}</option>
                <option value="music">{t('pjEdit.catMusic')}</option>
                <option value="business">{t('pjEdit.catBusiness')}</option>
                <option value="other">{t('projects.etc')}</option>
              </select>
            </div>

            <div className="form-group required">
              <label>{t('projectDetail.workType')}</label>
              <div className="radio-group">
                <div className="radio-option">
                  <input
                    type="radio"
                    id="outsourcing"
                    value="outsourcing"
                    checked={formData.workType === 'outsourcing'}
                    onChange={(e) => handleInputChange('workType', e.target.value)}
                  />
                  <label htmlFor="outsourcing">{t('pjEdit.outsourcing')}</label>
                </div>
                <div className="radio-option">
                  <input
                    type="radio"
                    id="remote"
                    value="remote"
                    checked={formData.workType === 'remote'}
                    onChange={(e) => handleInputChange('workType', e.target.value)}
                  />
                  <label htmlFor="remote">{t('pjCreate.workRemote')}</label>
                </div>
                <div className="radio-option">
                  <input
                    type="radio"
                    id="onsite"
                    value="onsite"
                    checked={formData.workType === 'onsite'}
                    onChange={(e) => handleInputChange('workType', e.target.value)}
                  />
                  <label htmlFor="onsite">{t('pjCreate.workOnsite')}</label>
                </div>
                <div className="radio-option">
                  <input
                    type="radio"
                    id="hybrid"
                    value="hybrid"
                    checked={formData.workType === 'hybrid'}
                    onChange={(e) => handleInputChange('workType', e.target.value)}
                  />
                  <label htmlFor="hybrid">{t('pjEdit.hybrid')}</label>
                </div>
              </div>
            </div>

            <div className="form-group required">
              <label>{t('pjCreate.titleLabel')}</label>
              <input
                type="text"
                value={formData.title}
                onChange={(e) => handleInputChange('title', e.target.value)}
                placeholder={t('pjEdit.titlePlaceholder')}
                maxLength={100}
              />
              <small>{formData.title.length}/100</small>
            </div>

            <div className="form-group required">
              <label>{t('pjEdit.shortDesc')}</label>
              <textarea
                value={formData.shortDescription}
                onChange={(e) => handleInputChange('shortDescription', e.target.value)}
                placeholder={t('pjEdit.shortDescPlaceholder')}
                maxLength={500}
              />
              <small>{formData.shortDescription.length}/500</small>
            </div>
          </div>

          {/* 일정 및 예산 */}
          <div className="form-section">
            <h2>{t('pjEdit.scheduleBudget')}</h2>

            <div className="form-row">
              <div className="form-group">
                <label>{t('pjCreate.startDate')}</label>
                <input
                  type="date"
                  value={formData.startDate}
                  onChange={(e) => handleInputChange('startDate', e.target.value)}
                  min="1920-01-01"
                  max="2099-12-31"
                />
              </div>
              <div className="form-group">
                <label>{t('pjEdit.deadline')}</label>
                <input
                  type="date"
                  value={formData.deadline}
                  onChange={(e) => handleInputChange('deadline', e.target.value)}
                  min="1920-01-01"
                  max="2099-12-31"
                />
              </div>
            </div>

            <div className="form-group">
              <label>{t('pjEdit.duration')}</label>
              <input
                type="number"
                value={formData.durationDays}
                onChange={(e) => handleInputChange('durationDays', parseInt(e.target.value) || 0)}
                min="1"
                max="365"
              />
            </div>

            <div className="form-group required">
              <label>{t('pjCreate.budgetType')}</label>
              <select
                value={formData.budgetType}
                onChange={(e) => handleInputChange('budgetType', e.target.value)}
              >
                <option value="project">{t('pjEdit.budgetFixed')}</option>
                <option value="hourly">{t('pjEdit.hourly')}</option>
                <option value="daily">{t('pjEdit.daily')}</option>
                <option value="negotiable">{t('projects.negotiable')}</option>
              </select>
            </div>

            <div className="form-row">
              <div className="form-group required">
                <label>{t('pjCreate.minBudget')}</label>
                <input
                  type="number"
                  value={formData.budgetMin}
                  onChange={(e) => handleInputChange('budgetMin', parseInt(e.target.value) || 0)}
                  min="0"
                />
              </div>
              <div className="form-group required">
                <label>{t('pjCreate.maxBudget')}</label>
                <input
                  type="number"
                  value={formData.budgetMax}
                  onChange={(e) => handleInputChange('budgetMax', parseInt(e.target.value) || 0)}
                  min="0"
                />
              </div>
            </div>
          </div>

          {/* 상세 정보 */}
          <div className="form-section">
            <h2>{t('pjCreate.stepDetails')}</h2>

            <div className="form-group">
              <label>{t('pjEdit.detailDesc')}</label>
              <textarea
                value={formData.description}
                onChange={(e) => handleInputChange('description', e.target.value)}
                placeholder={t('pjCreate.descPlaceholder')}
                rows={8}
                maxLength={2000}
              />
              <small>{formData.description.length}/2000</small>
            </div>

            <div className="form-group">
              <label>{t('pjEdit.expLevel')}</label>
              <select
                value={formData.experienceLevel}
                onChange={(e) => handleInputChange('experienceLevel', e.target.value)}
              >
                <option value="beginner">{t('pjEdit.beginner')}</option>
                <option value="intermediate">{t('pjCreate.intermediate')}</option>
                <option value="expert">{t('pjEdit.expert')}</option>
              </select>
            </div>

            <div className="form-group">
              <label>{t('pjEdit.skills')}</label>
              <div className="skills-input">
                <input
                  type="text"
                  value={skillInput}
                  onChange={(e) => setSkillInput(e.target.value)}
                  placeholder={t('pjEdit.skillPlaceholder')}
                  onKeyPress={(e) => e.key === 'Enter' && (e.preventDefault(), addSkill())}
                />
                <button type="button" onClick={addSkill}>
                  {t('common.add')}
                </button>
              </div>
              {formData.requiredSkills && formData.requiredSkills.length > 0 && (
                <div className="skills-list">
                  {formData.requiredSkills.map((skill, index) => (
                    <div key={index} className="skill-tag">
                      {skill}
                      <button
                        type="button"
                        onClick={() => removeSkill(index)}
                      >
                        ×
                      </button>
                    </div>
                  ))}
                </div>
              )}
            </div>
          </div>

          {error && (
            <div className="error-message" style={{ marginBottom: '20px' }}>
              {error}
            </div>
          )}

          <div className="form-actions">
            <button 
              type="button"
              className="btn btn-secondary"
              onClick={() => navigate('/my-projects')}
            >
              {t('common.cancel')}
            </button>
            <button
              type="button"
              className="btn btn-primary"
              onClick={handleSubmit}
              disabled={saving}
            >
              {saving ? t('svEdit.saving') : t('pjEdit.title')}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default ProjectEditPage;